import React, { useRef } from 'react';
import { injectIntl, useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import btnClose from '../../images/common/close.svg';
import * as actionTypes from '../../actions/actionTypes';
import updateTags from '../../actions/api/tag/updateTags';
import deleteTag from '../../actions/api/tag/deleteTag';
import {
  Main,
  BaseDialog,
  Contents,
  Header,
  CloseButton,
  CloseButtonImage,
} from '../../styles/common/Dialog';
import {
  ItemWrapper,
  ItemTitle,
  ItemInfo,
  InputArea,
  RadioItemInfo,
  RadioArea,
  RadioLabel,
  ButtonWrapper,
  CancelButton,
  DeleteButton,
  UpdateButton,
  MessageWrapper,
  MessageItem,
} from './styles/EditMasterDialogStyles';
import ErrorMsgArea from '../../styles/common/ErrorMessage';
import getAllTagsUpToLevel from './functions/getAllTagsUpToLevel';

const onCloseDialog = dispatch => {
  dispatch({ type: actionTypes.CLOSE_EDIT_MASTER_DIALOG });
};

// 編集ダイアログ生成
const createEditView = params => {
  const {
    dispatch,
    apiToken,
    formatMessage,
    selectedTag,
    tagNameRef,
    isUsableRef,
    handleUsageSettingChange,
    showEditMasterDialogErrorMsg,
    selectedTagIds,
    selectedTagSetId,
    maxDepth,
  } = params;

  const handleOpenConfirmDialog = () => {
    dispatch({
      type: actionTypes.SWITCH_SHOW_MODE_EDIT_MASTER_DIALOG,
      nextShowMode: 'confirm',
    });
  };

  const handleUpdateTag = async () => {
    if (tagNameRef.current.value === '') {
      dispatch({
        type: actionTypes.SWITCH_SHOW_EDIT_MASTER_DIALOG_ERROR_MSG,
      });
      return;
    }
    await dispatch(
      updateTags(apiToken, {
        id: selectedTag.id,
        name: tagNameRef ? tagNameRef.current.value : selectedTag.name,
        isUsable: isUsableRef.current,
      }),
    );

    dispatch(
      getAllTagsUpToLevel(apiToken, selectedTagIds, selectedTagSetId, maxDepth),
    );
  };

  return (
    <div>
      <ItemWrapper>
        <ItemTitle>
          <FormattedMessage id="master.EditMasterDialog.title.tagName" />
        </ItemTitle>
        <ItemInfo>
          <InputArea
            id="updateDeviceNickname"
            type="text"
            defaultValue={selectedTag.name}
            ref={tagNameRef}
            placeholder={formatMessage({
              id: 'master.EditMasterDialog.title.tagName.placeholder',
            })}
          />
        </ItemInfo>
      </ItemWrapper>
      <ItemWrapper>
        <ItemTitle>
          <FormattedMessage id="master.EditMasterDialog.title.configuration" />
        </ItemTitle>
        <RadioItemInfo>
          <RadioArea
            id="available"
            type="radio"
            name="isUsable"
            defaultChecked={selectedTag.isUsable}
            onClick={() => handleUsageSettingChange(true)}
          />
          <RadioLabel htmlFor="available">
            <FormattedMessage id="master.EditMasterDialog.item.configuration.available" />
          </RadioLabel>
          <RadioArea
            id="notAvailable"
            type="radio"
            name="isUsable"
            defaultChecked={!selectedTag.isUsable}
            onClick={() => handleUsageSettingChange(false)}
          />
          <RadioLabel htmlFor="notAvailable">
            <FormattedMessage id="master.EditMasterDialog.item.configuration.notAvailable" />
          </RadioLabel>
        </RadioItemInfo>
      </ItemWrapper>
      {showEditMasterDialogErrorMsg && (
        <ErrorMsgArea>
          <FormattedMessage id="master.EditMasterDialog.item.tagName.formatError" />
        </ErrorMsgArea>
      )}
      <ButtonWrapper>
        <CancelButton onClick={() => onCloseDialog(dispatch)}>
          <FormattedMessage id="master.EditMasterDialog.item.button.cancel" />
        </CancelButton>
        <DeleteButton onClick={handleOpenConfirmDialog}>
          <FormattedMessage id="master.EditMasterDialog.item.button.delete" />
        </DeleteButton>
        <UpdateButton onClick={handleUpdateTag}>
          <FormattedMessage id="master.EditMasterDialog.item.button.update" />
        </UpdateButton>
      </ButtonWrapper>
    </div>
  );
};

// 削除確認ダイアログ生成
const createConfirmView = params => {
  const {
    dispatch,
    apiToken,
    formatMessage,
    selectedTag,
    selectedTagIds,
    selectedTagSetId,
    maxDepth,
  } = params;
  const handleDeleteDialog = async () => {
    await dispatch(deleteTag(apiToken, selectedTag.id));
    dispatch(
      getAllTagsUpToLevel(apiToken, selectedTagIds, selectedTagSetId, maxDepth),
    );
    onCloseDialog(dispatch);
  };
  return (
    <div>
      <MessageWrapper>
        <MessageItem>
          {formatMessage({
            id: 'master.EditMasterDialog.confirm.message1',
          }).replace('{tag}', selectedTag.name)}
        </MessageItem>
        <MessageItem>
          <FormattedMessage id="master.EditMasterDialog.confirm.message2" />
        </MessageItem>
      </MessageWrapper>
      <ButtonWrapper>
        <CancelButton onClick={() => onCloseDialog(dispatch)}>
          <FormattedMessage id="master.EditMasterDialog.item.button.cancel" />
        </CancelButton>
        <DeleteButton onClick={handleDeleteDialog}>
          <FormattedMessage id="master.EditMasterDialog.item.button.delete" />
        </DeleteButton>
      </ButtonWrapper>
    </div>
  );
};

/**
 * タグ編集ダイアログ
 */
const EditMasterDialog = () => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();
  const {
    apiToken,
    selectedTagIds,
    selectedTagSetId,
    openEditMasterDialog,
    showEditMasterDialogErrorMsg,
    selectedTag,
    maxDepth,
  } = useSelector(state => ({
    apiToken: state.main.apiToken,
    selectedTagIds: state.master.selectedTagIds,
    selectedTagSetId: state.master.selectedTagSetId,
    openEditMasterDialog: state.master.openEditMasterDialog,
    showEditMasterDialogErrorMsg: state.master.showEditMasterDialogErrorMsg,
    selectedTag: state.master.selectedTag,
    maxDepth: state.master.maxDepth,
  }));
  const tagNameRef = useRef(null);
  const isUsableRef = useRef(selectedTag.isUsable);
  const handleUsageSettingChange = isUsable => {
    isUsableRef.current = isUsable;
  };

  const params = {
    dispatch,
    apiToken,
    formatMessage,
    selectedTag,
    tagNameRef,
    isUsableRef,
    handleUsageSettingChange,
    showEditMasterDialogErrorMsg,
    selectedTagIds,
    selectedTagSetId,
    maxDepth,
  };
  return (
    <Main
      id="closeConfirmEditMasterDialog"
      role="presentation"
      onClick={() => onCloseDialog(dispatch)}
    >
      <BaseDialog role="presentation" onClick={e => e.stopPropagation()}>
        <Contents>
          <Header>
            <FormattedMessage id="master.EditMasterDialog.title" />
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => onCloseDialog(dispatch)}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          </Header>
          {openEditMasterDialog === 'edit' && createEditView(params)}
          {openEditMasterDialog === 'confirm' && createConfirmView(params)}
        </Contents>
      </BaseDialog>
    </Main>
  );
};

export default injectIntl(EditMasterDialog);
